import React from 'react'
import { Box, Chip, Link } from '@mui/material'

import { STATUS_DIC, TASK_TYPE_DIC, TASK_TYPES, PROD_TYPE_DIC } from '../../constants/constants'
import {
  UI_COLOR_BLACK,
  UI_COLOR_CHIP_ACCENT_BG,
  UI_COLOR_CHIP_ACCENT_TEXT,
  UI_COLOR_CHIP_ASSIGNMENT_BG,
  UI_COLOR_CHIP_ASSIGNMENT_TEXT,
  UI_COLOR_DANGER_BADGE_BG,
  UI_COLOR_DANGER_BADGE_TEXT,
  UI_COLOR_SURFACE,
  UI_COLOR_SURFACE_300,
  UI_COLOR_TEXT_MUTED_LIGHT
} from '../../../constants/uiColors'
import { capitalizeFirstLetterFrom } from '../../../helpers/utils'
import GoogleIcon from '../../../components/icon/GoogleIcon'
import { handleDownloadImage } from '../../forms/surveys/helpers/SurveyComponentsHelper'
import { POS_TASK_STATUS_UI_MAP } from './posDetailsConstants'

const QUALIFIER_LABELS = {
  qualifier_2: 'Atributo A',
  qualifier_3: 'Atributo B',
  qualifier_4: 'Atributo C',
  qualifier_5: 'Atributo D',
  qualifier_6: 'Atributo E'
}

const asArray = (value) => (Array.isArray(value) ? value : [])
const COMPACT_LIST_ROW_HEIGHT = 22
const COMPACT_LIST_ROW_GAP = 8
const COMPACT_LIST_TOGGLE_LABELS = {
  EXPAND: 'Ver más...',
  COLLAPSE: 'Ver menos'
}

const MANDATORY_TASK_CHIP_SX = {
  minWidth: 48,
  height: 22,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 800,
  '& .MuiChip-label': {
    px: 1.4
  }
}

const COMPACT_LIST_CONTAINER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  maxHeight: 92,
  overflowY: 'auto',
  pr: 0.5
}

const COMPACT_LIST_CHIP_SX = {
  maxWidth: '100%',
  height: 22,
  borderRadius: 999,
  backgroundColor: '#D9D5D0',
  color: '#4B4B4B',
  fontSize: 11,
  fontWeight: 500,
  textTransform: 'uppercase',
  '& .MuiChip-label': {
    display: 'block',
    maxWidth: '100%',
    px: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}

const renderMandatoryTaskChip = (isMandatoryTask) => (
  <Chip
    label={isMandatoryTask ? 'SI' : 'NO'}
    size="small"
    sx={{
      ...MANDATORY_TASK_CHIP_SX,
      backgroundColor: isMandatoryTask ? UI_COLOR_DANGER_BADGE_BG : '#F3F4F6',
      color: isMandatoryTask ? UI_COLOR_DANGER_BADGE_TEXT : '#6B7280'
    }}
  />
)

const COMPACT_LIST_CHIP_ACCENT_SX = {
  ...COMPACT_LIST_CHIP_SX,
  backgroundColor: UI_COLOR_CHIP_ACCENT_BG,
  color: UI_COLOR_CHIP_ACCENT_TEXT
}

const COMPACT_LIST_CHIP_ASSIGNMENT_SX = {
  ...COMPACT_LIST_CHIP_SX,
  backgroundColor: UI_COLOR_CHIP_ASSIGNMENT_BG,
  color: UI_COLOR_CHIP_ASSIGNMENT_TEXT
}

const COMPACT_LIST_CHIP_PRODUCT_SX = {
  ...COMPACT_LIST_CHIP_SX,
  backgroundColor: UI_COLOR_SURFACE,
  color: UI_COLOR_BLACK,
  border: `1px solid ${UI_COLOR_SURFACE_300}`
}

const CompactList = ({ items, maxLines = null, chipSx = COMPACT_LIST_CHIP_SX }) => {
  const [expanded, setExpanded] = React.useState(false)
  const [hasOverflow, setHasOverflow] = React.useState(false)
  const listRef = React.useRef(null)
  const safeMaxLines = maxLines ? Math.max(1, Number(maxLines) || 1) : null
  const collapsedMaxHeight = safeMaxLines
    ? COMPACT_LIST_ROW_HEIGHT * safeMaxLines + COMPACT_LIST_ROW_GAP * (safeMaxLines - 1)
    : COMPACT_LIST_CONTAINER_SX.maxHeight

  React.useLayoutEffect(() => {
    const element = listRef.current
    if (!element) return

    const checkOverflow = () => {
      setHasOverflow(element.scrollHeight > element.clientHeight + 1)
    }

    checkOverflow()

    if (typeof ResizeObserver === 'undefined') return undefined

    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [items, expanded, collapsedMaxHeight])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
        minWidth: 0
      }}
    >
      <Box
        ref={listRef}
        sx={{
          ...COMPACT_LIST_CONTAINER_SX,
          maxHeight: expanded ? 'none' : collapsedMaxHeight,
          minWidth: 0
        }}
      >
        {items.map((item, index) => (
          <Chip
            key={`${item}-${index}`}
            label={item}
            title={item}
            size="small"
            sx={chipSx}
          />
        ))}
      </Box>

      {safeMaxLines && (hasOverflow || expanded) && (
        <Link
          component="button"
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            color: '#1E40AF',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.2,
            p: 0,
            minWidth: 0
          }}
        >
          {expanded ? COMPACT_LIST_TOGGLE_LABELS.COLLAPSE : COMPACT_LIST_TOGGLE_LABELS.EXPAND}
        </Link>
      )}
    </Box>
  )
}

const renderCompactList = (items, emptyValue = '-', options = {}) => {
  const safeItems = asArray(items).filter(Boolean)

  if (safeItems.length === 0) {
    return emptyValue
  }

  return <CompactList items={safeItems} maxLines={options.maxLines} chipSx={options.chipSx} />
}

export const normalizeCompetitionProducts = (competitionProducts) => {
  if (Array.isArray(competitionProducts)) {
    return competitionProducts
  }

  if (typeof competitionProducts === 'string') {
    try {
      const parsedCompetitionProducts = JSON.parse(competitionProducts)
      return Array.isArray(parsedCompetitionProducts) ? parsedCompetitionProducts : []
    } catch (error) {
      return []
    }
  }

  return []
}

export const getFormattedDateOnly = (createdDate) => {
  if (!createdDate) return '-'
  const d = new Date(createdDate)
  return d.toLocaleDateString(undefined, { timeZone: 'utc' })
}

export const getFormattedDate = (createdDate) => {
  if (!createdDate) return '-'
  const d = new Date(createdDate)
  return d.toLocaleString()
}

const getClients = (clientNames) => {
  return renderCompactList(clientNames, 'No se encontraron clientes', {
    chipSx: COMPACT_LIST_CHIP_ACCENT_SX
  })
}

const getQualifiers = (qualifiers) => {
  return renderCompactList(qualifiers, '-', {
    maxLines: 2,
    chipSx: COMPACT_LIST_CHIP_ACCENT_SX
  })
}

const renderRecurrenceValue = (recurrenceDescription) => {
  const normalizedDescription = capitalizeFirstLetterFrom(recurrenceDescription) || '-'
  const weeklyMatch = normalizedDescription.match(/^(Cada(?: \d+)? semanas?) los (.+)$/i)

  if (!weeklyMatch) {
    return {
      value: normalizedDescription,
      secondaryValue: null,
      inlineLabel: false
    }
  }

  const [, frequencyText, daysText] = weeklyMatch
  const days = daysText
    .replace(/\s+y\s+/g, ', ')
    .split(',')
    .map((day) => day.trim())
    .filter(Boolean)

  if (days.length === 0) {
    return {
      value: normalizedDescription,
      secondaryValue: null,
      inlineLabel: false
    }
  }

  return {
    value: frequencyText,
    secondaryValue: renderCompactList(days),
    inlineLabel: true
  }
}

export const getPosTaskStatusUI = (activity = {}) => {
  const statusKey = activity.suspended ? 'suspended' : activity.status
  return (
    POS_TASK_STATUS_UI_MAP[statusKey] || {
      label: (STATUS_DIC[statusKey] || statusKey || '-').toString().toUpperCase(),
      statusKey: statusKey || 'default'
    }
  )
}

export const buildPosTaskHeaderData = (activity = {}) => {
  const description = activity.description?.trim()
  const shortUuid = activity.short_uuid || '-'

  return {
    ...activity,
    display_title: description || shortUuid,
    display_task_type: TASK_TYPE_DIC[activity.task_type] || activity.task_type || '-',
    display_start_date: getFormattedDateOnly(activity.start_date),
    display_end_date: activity.end_date ? getFormattedDateOnly(activity.end_date) : 'Indefinida'
  }
}

export const buildPosTaskSummaryFields = (activity = {}) => {
  const fields = [
    {
      label: 'Obligatoria',
      value: renderMandatoryTaskChip(activity.is_mandatory_task)
    },
    {
      label: 'Cantidad de subtareas',
      value: String(activity.task_quantity ?? 0)
    }
  ]

  const recurrenceValue = renderRecurrenceValue(activity.recurrence_description)

  fields.push(
    {
      label: 'Recurrencia',
      value: recurrenceValue.value,
      secondaryValue: recurrenceValue.secondaryValue,
      inlineLabel: recurrenceValue.inlineLabel
    },
    {
      label: 'Fecha de creación',
      value: getFormattedDate(activity.created_at)
    },
    {
      label: 'Fecha de actualización',
      value: getFormattedDate(activity.updated_at)
    }
  )

  return fields
}

export const buildPosTaskScopeFields = (activity = {}) => {
  const qualifier1 = asArray(activity.qualifier_1)
  const fields = []

  if (qualifier1.length > 0) {
    fields.push({
      label: 'Cliente(s)',
      value: getClients(activity.client_names),
      fullWidth: true,
      inlineLabel: true
    })
  }

  Object.entries(QUALIFIER_LABELS).forEach(([key, label]) => {
    if (asArray(activity[key]).length > 0) {
      fields.push({
        label,
        value: getQualifiers(activity[key]),
        fullWidth: true,
        inlineLabel: true
      })
    }
  })

  return fields
}

export const renderPosTaskAssignmentEmptyState = () => (
  <Box
    sx={{
      fontSize: 13,
      fontStyle: 'italic',
      color: UI_COLOR_TEXT_MUTED_LIGHT
    }}
  >
    Sin usuarios asignados
  </Box>
)

export const buildPosTaskAssignmentFields = (activity = {}) => {
  const userNames = asArray(activity.user_names)
  const fields = []

  if (userNames.length === 0) {
    return fields
  }

  fields.push({
    label: 'Usuario(s) asignados',
    value: renderCompactList(userNames, 'N/A', {
      chipSx: COMPACT_LIST_CHIP_ASSIGNMENT_SX
    }),
    hideLabel: true
  })

  return fields
}

export const getPosTaskExtraTitle = (activity = {}) => {
  const taskType = activity.task_type

  if (taskType === TASK_TYPES.inventory.key) {
    return 'CAPTURA DE INVENTARIO'
  }

  if (taskType === TASK_TYPES.survey.key) {
    return 'ENCUESTA'
  }

  if (taskType === TASK_TYPES.price_capture.key) {
    return 'CAPTURA DE PRECIOS'
  }

  if (taskType === TASK_TYPES.planimetry.key) {
    return 'PLANIMETRÍA'
  }

  return 'DATOS ESPECÍFICOS'
}

const getProductsSelectionValue = (activity = {}, options = {}) => {
  if (activity.products_selection_type === 'brand') {
    return renderCompactList(activity.brand_name ? [activity.brand_name] : [], '-', options)
  }
  if (activity.products_selection_type === 'category') {
    return renderCompactList(activity.category_name ? [activity.category_name] : [], '-', options)
  }
  if (activity.products_selection_type === 'individual_products') {
    return renderCompactList(activity.product_names, '-', options)
  }
  if (!activity.products_selection_type) {
    return '-'
  }
  return '-'
}

const renderPlanogramImage = (activity = {}, { onShowImage } = {}) => {
  const imageUrl = activity.image_url

  if (!imageUrl) {
    return '-'
  }

  const downloadName = activity.short_uuid || activity.uuid || 'planimetria'

  return (
    <Box
      sx={{
        position: 'relative',
        width: 180,
        height: 180,
        borderRadius: '28px',
        overflow: 'hidden',
        backgroundColor: '#F8FAFC',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => handleDownloadImage(imageUrl, downloadName)}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          border: '1px solid #475C88',
          borderRadius: '50%',
          backgroundColor: '#475C88',
          color: '#FFFFFF',
          p: 0,
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.18)',
          '&:hover': {
            backgroundColor: '#3E5077',
            borderColor: '#3E5077'
          }
        }}
        aria-label="Descargar imagen de referencia"
        title="Descargar imagen"
      >
        <GoogleIcon name="download" size={18} color="#FFFFFF" />
      </Box>

      <Box
        component="img"
        src={imageUrl}
        alt="Imagen de referencia de planimetría"
        onClick={() => onShowImage?.(true)}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          cursor: 'zoom-in',
          backgroundColor: '#FFFFFF'
        }}
      />
    </Box>
  )
}

export const buildPosTaskExtraFields = (activity = {}, { onShowImage } = {}) => {
  const fields = []
  const taskType = activity.task_type

  if (taskType !== 'survey' && taskType !== 'price_capture') {
    fields.push({
      label: 'Tipo de productos',
      value:
        PROD_TYPE_DIC[activity.products_selection_type] ||
        (activity.products_selection_type
          ? activity.products_selection_type
          : 'No se han agregado productos')
    })
    fields.push({
      label: 'Producto(s) de la tarea',
      value: getProductsSelectionValue(activity, { chipSx: COMPACT_LIST_CHIP_PRODUCT_SX }),
      fullWidth: activity.products_selection_type === 'individual_products'
    })
  }

  if (taskType === 'price_capture') {
    fields.push({
      label: 'Producto(s) propio(s)',
      value: renderCompactList(
        asArray(activity.product_names).map((product) => product.replace(/^(\w+)/, '$1 -')),
        'N/A',
        { chipSx: COMPACT_LIST_CHIP_PRODUCT_SX }
      )
    })
    fields.push({
      label: 'Producto(s) competencia',
      value: renderCompactList(
        asArray(activity.competition_products).map(
          (product) =>
            `${(product.name || '').toUpperCase()} - ${(product.package || '').toUpperCase()}`
        ),
        'N/A',
        { chipSx: COMPACT_LIST_CHIP_PRODUCT_SX }
      )
    })
  }

  if (taskType === 'planimetry') {
    fields.push({
      label: 'Instrucciones',
      fullWidth: Boolean(activity.instructions),
      value: (
        <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
          {activity.instructions || '-'}
        </Box>
      )
    })
    fields.push({
      label: 'Imagen de referencia',
      fullWidth: true,
      value: renderPlanogramImage(activity, { onShowImage })
    })
  }

  return fields
}
